/**
 * User Files Service - API client for user file management.
 *
 * This service handles:
 * - File listing and retrieval
 * - Storage quota information
 * - File record creation after upload
 * - Signed URL generation
 *
 * Issue: https://github.com/lexomat-ai/lexomat/issues/534
 */

import ApiService, { type ApiResponse } from './api-services';

// =============================================================================
// Types
// =============================================================================

interface UserFile {
  id: number;
  user_id: string;
  storage_key: string;
  original_filename: string;
  size_bytes: number;
  mime_type: string;
  file_extension: string | null;
  status: 'active' | 'read_only' | 'deleting' | 'deleted';
  source: 'upload' | 'chat' | 'research';
  source_session_id: string | null;
  /**
   * True = working copy only (not in the library, eligible for the
   * session-scoped purge cron); False = saved to the user's library.
   * Set by resolve_session_scoped() on upload and reconciled by
   * apply_save_intent_to_files() (#6366/#6500). Per-file, not to be confused
   * with the user-level `auto_save_chat_uploads` preference.
   */
  session_scoped: boolean;
  created_at: string;
  updated_at: string;
  retire_at: string | null;
  /**
   * One-time link to the Storage file browser entry created when this chat
   * upload was auto-saved/synced (#7520). Null = never synced (still
   * session-scoped, pre-dates this field, or the best-effort backend sync
   * failed). Frozen snapshot — set once, never re-checked against Storage
   * afterward.
   */
  synced_storage_node_id: number | null;
  download_url: string | null;
}

interface FileListResponse {
  files: UserFile[];
  total: number;
  page: number;
  limit: number;
  has_more: boolean;
}

/**
 * Per-upload choice from the first-upload contextual prompt (#6366).
 *   'save'    — keep this upload in the library (this file only)
 *   'always'  — keep it AND flip the auto_save preference to true (FE writes pref)
 *   'session' — keep it as a working copy for this chat only
 * Only honoured by the backend when AUTO_SAVE_OPT_IN_ENABLED is on.
 */
export type SaveIntent = 'save' | 'session' | 'always';

interface CreateFileRecordRequest {
  storage_key: string;
  original_filename: string;
  size_bytes: number;
  mime_type: string;
  source?: 'upload' | 'chat' | 'research';
  source_session_id?: string | undefined;
  save_intent?: SaveIntent | undefined;
}

interface UploadConfig {
  /** Whether the privacy-by-default first-upload prompt should be shown (#6366). */
  auto_save_opt_in_enabled: boolean;
  /**
   * Whether the per-file sync-status badge/"Save to library" action and the
   * Spis-scoped auto-save warning modal should be shown. Dark by default
   * pending @citbaj/DPO review of the new copy.
   */
  attachment_controls_enabled: boolean;
}

interface SignedUrlResponse {
  signed_urls: Record<string, string | null>;
}

interface UploadUrlResponse {
  upload_url: string;
  storage_key: string;
}

interface DownloadAllResponse {
  files: Array<{
    id: number;
    storage_key: string;
    original_filename: string;
    size_bytes: number;
    mime_type: string;
    download_url: string | null;
  }>;
  total_size_bytes: number;
  file_count: number;
}

interface DeleteAllResponse {
  success: boolean;
  deleted_count: number;
  message: string;
}

// =============================================================================
// Service
// =============================================================================

class UserFilesServices extends ApiService {
  constructor() {
    super('/user-files');
  }

  /**
   * List user's files with pagination.
   * User ID is derived from the auth session on the backend.
   */
  listFiles(page = 1, limit = 20): ApiResponse<FileListResponse> {
    return this.getData(`?page=${page}&limit=${limit}`);
  }

  /**
   * Get a single file by ID.
   * User ID is derived from the auth session on the backend.
   */
  getFile(fileId: number): ApiResponse<UserFile> {
    return this.getData(`/${fileId}`);
  }

  /**
   * Get upload-related feature flags (e.g. the #6366 auto-save opt-in prompt).
   * User ID is derived from the auth session on the backend.
   */
  getUploadConfig(): ApiResponse<UploadConfig> {
    return this.getData('/upload-config');
  }

  /**
   * Create a file record after successful upload.
   * User ID is derived from the auth session on the backend.
   */
  createFileRecord(data: CreateFileRecordRequest): ApiResponse<UserFile> {
    return this.postData('/record', data);
  }

  /**
   * Reconcile already-uploaded chat files to the first-upload prompt choice
   * (#6500 race fix). The first file of a session is created before the user
   * answers the prompt; once they choose, this updates its session_scoped stamp
   * so "Just this chat" / "Save" wins. No-op on the backend unless the opt-in
   * flag is on. User ID is derived from the auth session.
   */
  applySaveIntent(
    fileIds: number[],
    saveIntent: SaveIntent
  ): ApiResponse<{
    updated: number;
    /**
     * file_id (as string, matching JSON object-key coercion) -> the newly
     * linked storage_nodes id, for files this call synced to Storage (#7520).
     * Only present for files that weren't already synced.
     */
    synced_storage_node_ids: Record<string, number>;
  }> {
    return this.postData('/record/apply-intent', {
      file_ids: fileIds,
      save_intent: saveIntent,
    });
  }

  /**
   * Delete a file (soft delete).
   * User ID is derived from the auth session on the backend.
   */
  deleteFile(
    fileId: number
  ): ApiResponse<{ success: boolean; message: string }> {
    return this.deleteData(`/${fileId}`, {});
  }

  /**
   * Convert public URLs to signed URLs.
   * Used for backwards compatibility with existing chat.data_sources.
   * User ID is derived from the auth session on the backend.
   */
  getSignedUrls(urls: string[]): ApiResponse<SignedUrlResponse> {
    return this.postData('/signed-urls', { urls });
  }

  /**
   * Get a signed upload URL for direct file upload.
   * User ID is derived from the auth session on the backend.
   */
  getUploadUrl(
    filename: string,
    sizeBytes: number,
    mimeType: string
  ): ApiResponse<UploadUrlResponse> {
    return this.postData('/upload-url', {
      filename,
      size_bytes: sizeBytes,
      mime_type: mimeType,
    });
  }

  /**
   * Get all files with signed URLs for bulk download.
   * User ID is derived from the auth session on the backend.
   */
  downloadAll(): ApiResponse<DownloadAllResponse> {
    return this.postData('/download-all', {});
  }

  /**
   * Delete all user's files (soft delete).
   * User ID is derived from the auth session on the backend.
   */
  deleteAll(): ApiResponse<DeleteAllResponse> {
    return this.postData('/delete-all', {});
  }
}

// Export singleton instance
const userFilesServices = new UserFilesServices();
export default userFilesServices;

// =============================================================================
// Utility Functions
// =============================================================================

/**
 * Format bytes to human-readable string.
 */
export function formatBytes(bytes: number, decimals = 2): string {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${Number.parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
}

/**
 * Check if a URL is a Supabase public URL that needs to be converted.
 */
export function isSupabasePublicUrl(url: string): boolean {
  return url.includes('/storage/v1/object/public/users/');
}

/**
 * Extract storage key from a Supabase public URL.
 */
export function extractStorageKey(publicUrl: string): string | null {
  const match = publicUrl.match(/\/storage\/v1\/object\/public\/users\/(.+)$/);
  return match?.[1] ?? null;
}
